import {
  FlexBox,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { useMemo } from 'react';
import { useEffectOnce } from 'react-use';

import { KeyboardKeyCombinations } from 'components/keyboard';
import { useKeyCombinationRecorder } from 'utils/keyboard';
import { createAppModal } from 'utils/modal';
import { useNavigatePrompt } from 'utils/url/useNavigatePrompt';

import styles from './keyCombinationRecorderModal.module.scss';

/** Represents the modal for recording key combinations. */
export const KeyCombinationRecorderModal = createAppModal(
  { type: 'app:keyCombinationRecorder' },
  ({ isOpen, modal, meta, hide }) => {
    const { keyCombination, startRecording, stopRecording } =
      useKeyCombinationRecorder();

    const title = modal.arg.title ?? 'Keyboard key combination recorder';
    const description = modal.arg.description;

    const handleCancel = () => {
      meta?.resolve({
        isConfirmed: false,
        isCancelled: true,
      });

      hide?.();
    };

    const handleConfirm = () => {
      meta?.resolve({
        isConfirmed: true,
        isCancelled: false,
        keyCombination,
      });

      hide?.();
    };

    // Handles starting and stopping the recording when the modal state changes.
    useEffectOnce(() => {
      startRecording();

      return () => {
        stopRecording();
      };
    });

    useNavigatePrompt();

    const conflict = useMemo(() => {
      if (keyCombination && modal.arg.conflictFn) {
        return modal.arg.conflictFn(keyCombination);
      }

      return false;
    }, [keyCombination, modal.arg]);

    const hasKeyCombination = !!keyCombination;
    const hasKey = !!keyCombination?.binding?.[1];
    const isKeyCombinationInConflict = !!conflict;
    const isValid = hasKey && !isKeyCombinationInConflict;
    const isConfirmDisabled = !isValid;

    return (
      <Modal isOpen={isOpen} onClose={handleCancel} className={styles.root}>
        <ModalHead title={title} onClose={() => handleCancel()} />

        <ModalBody>
          <FlexBox flexDirection="column" gap="300">
            {description}

            <FlexBox
              className={classnames(styles.preview, {
                [styles.isValid]: hasKeyCombination && isValid,
                [styles.isInvalid]: hasKeyCombination && !isValid,
              })}
              alignItems="center"
              justifyContent="center"
            >
              {keyCombination ? (
                <KeyboardKeyCombinations
                  keyCombinations={[keyCombination]}
                  variant="rich"
                />
              ) : (
                'Press a combination of keys to record'
              )}
            </FlexBox>

            {typeof conflict !== 'boolean' && conflict.message && (
              <FlexBox>{conflict.message}</FlexBox>
            )}
          </FlexBox>
        </ModalBody>

        <ModalFoot
          cancelName={modal.arg.cancel?.label ?? 'Cancel'}
          cancelOnClick={handleCancel}
          cancelProps={{
            variant: 'secondary',
          }}
          confirmName={modal.arg.confirm?.label ?? 'Confirm'}
          confirmOnClick={handleConfirm}
          confirmProps={{
            variant: modal.arg.confirm?.variant,
            disabled: isConfirmDisabled,
          }}
        />
      </Modal>
    );
  },
);
